/**
 * LessonLink - 連絡帳モジュール
 */
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { showMessage, clearMessage, formatLessonDateShort, formatNoteStatus, showPage, pages, canEditInstructorNote, canEditStudentNote, isInstructor } from './utils';
import { getUserDisplayNames, getCurrentUserRole, getCurrentUserEmail } from './settings';

type Client = ReturnType<typeof generateClient<Schema>>;
let client: Client;

function getClient() {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
}

const PAGE_SIZE = 10;
let allLessons: Schema['Lesson']['type'][] = [];
let visibleCount = 0;
// email → displayName のマッピング
let contactStudentNameMap: Map<string, string> = new Map();

/**
 * 連絡帳機能を初期化
 */
export function initContact() {
  const loadMoreBtn = document.getElementById('contact-load-more-btn') as HTMLButtonElement;
  const backBtn = document.getElementById('contact-back-btn') as HTMLButtonElement;

  // 一覧の「詳細を見る」イベント委譲
  const listContainer = document.getElementById('contact-list') as HTMLElement;
  listContainer.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest('[data-contact-lesson-id]') as HTMLElement;
    if (!btn) return;
    const lessonId = btn.dataset.contactLessonId!;
    await showDetail(lessonId);
  });

  // もっと見る
  loadMoreBtn.addEventListener('click', () => {
    visibleCount += PAGE_SIZE;
    renderList();
    const items = listContainer.querySelectorAll('li');
    if (items.length > visibleCount - PAGE_SIZE) {
      const newFirst = items[visibleCount - PAGE_SIZE];
      if (newFirst) {
        (newFirst as HTMLElement).setAttribute('tabindex', '-1');
        (newFirst as HTMLElement).focus();
      }
    }
  });

  // 一覧に戻る
  backBtn.addEventListener('click', () => {
    document.getElementById('contact-detail-section')!.classList.add('hidden');
    document.getElementById('contact-list-section')!.classList.remove('hidden');
    const heading = document.getElementById('contact-heading') as HTMLElement;
    heading.setAttribute('tabindex', '-1');
    heading.focus();
  });

  // 詳細画面の編集・保存イベント委譲
  const detailSection = document.getElementById('contact-detail-section') as HTMLElement;
  detailSection.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;

    // 編集ボタン
    if (target.matches('[data-edit-field]')) {
      const field = target.dataset.editField!;
      startEdit(field);
      return;
    }

    // キャンセルボタン
    if (target.matches('[data-cancel-field]')) {
      const field = target.dataset.cancelField!;
      cancelEdit(field);
      return;
    }

    // 保存ボタン
    if (target.matches('[data-save-field]')) {
      const field = target.dataset.saveField!;
      await saveNote(field);
      return;
    }
  });
}

/**
 * 連絡帳一覧を読み込む
 */
export async function loadContactList() {
  // 常に一覧画面を表示（詳細画面が残っていたらリセット）
  document.getElementById('contact-list-section')!.classList.remove('hidden');
  document.getElementById('contact-detail-section')!.classList.add('hidden');

  try {
    const { data: lessons } = await getClient().models.Lesson.list();
    if (!lessons) return;

    const role = getCurrentUserRole();
    const myEmail = getCurrentUserEmail();

    // 生徒名マッピングを構築（全ロール共通で必要）
    const { data: users } = await getClient().models.User.list();
    contactStudentNameMap.clear();
    users?.forEach((u) => {
      if (u.email && u.displayName) {
        contactStudentNameMap.set(u.email, u.displayName);
      }
    });

    // 生徒の場合、自分の授業のみフィルター
    const filtered = isInstructor(role)
      ? lessons
      : lessons.filter((l) => l.studentOwner === myEmail);

    // 日付降順（新しい順）
    allLessons = filtered
      .filter((l) => l.date)
      .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());

    visibleCount = 0;
    visibleCount += PAGE_SIZE;
    renderList();
  } catch (error) {
    console.error('Load contact list error:', error);
    showMessage('連絡帳の読み込みに失敗しました');
  }
}

/**
 * 特定の授業の連絡帳詳細を直接表示（授業予定画面からの遷移用）
 */
export async function showContactForLesson(lessonId: string) {
  showPage(pages.pageContact);
  await loadContactList();
  await showDetail(lessonId);
}

function renderList() {
  const container = document.getElementById('contact-list') as HTMLElement;
  const emptyMsg = document.getElementById('contact-empty') as HTMLElement;
  const loadMoreBtn = document.getElementById('contact-load-more-btn') as HTMLButtonElement;

  if (allLessons.length === 0) {
    container.innerHTML = '';
    emptyMsg.classList.remove('hidden');
    loadMoreBtn.classList.add('hidden');
    return;
  }

  emptyMsg.classList.add('hidden');
  const visible = allLessons.slice(0, visibleCount);

  container.innerHTML = visible
    .map((l) => {
      const dateLabel = formatLessonDateShort(l.date!);
      const role = getCurrentUserRole();
      const studentName = isInstructor(role) && l.studentOwner
        ? ` — ${contactStudentNameMap.get(l.studentOwner) || l.studentOwner}`
        : '';
      // contactNote はリレーションなので非同期取得が必要だが、
      // 一覧では簡易表示のため、後から非同期で更新する
      return `<li class="py-3 border-b border-gray-200" data-lesson-item-id="${l.id}">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-medium">${dateLabel} の授業${studentName}</div>
            <div class="text-sm text-gray-500 mt-1" data-note-status="${l.id}">読み込み中...</div>
          </div>
          <button type="button"
                  data-contact-lesson-id="${l.id}"
                  class="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 rounded border border-blue-300 hover:bg-blue-50">
            詳細を見る
          </button>
        </div>
      </li>`;
    })
    .join('');

  // 記入状況を非同期で取得
  visible.forEach(async (l) => {
    try {
      const { data: notes } = await getClient().models.ContactNote.list({
        filter: { lessonId: { eq: l.id } },
      });
      const note = notes && notes.length > 0 ? notes[0] : null;
      const status = formatNoteStatus(note?.instructorNote, note?.studentNote);
      const statusEl = document.querySelector(`[data-note-status="${l.id}"]`);
      if (statusEl) {
        statusEl.textContent = `講師: ${status.instructor} / 生徒: ${status.student}`;
      }
    } catch {
      const statusEl = document.querySelector(`[data-note-status="${l.id}"]`);
      if (statusEl) {
        statusEl.textContent = '記入状況の取得に失敗';
      }
    }
  });

  if (allLessons.length > visibleCount) {
    loadMoreBtn.classList.remove('hidden');
  } else {
    loadMoreBtn.classList.add('hidden');
  }
}

// 現在表示中の詳細データ
let currentLessonId = '';
let currentNoteId: string | null = null;

async function showDetail(lessonId: string) {
  clearMessage();
  currentLessonId = lessonId;

  // 一覧を隠して詳細を表示
  document.getElementById('contact-list-section')!.classList.add('hidden');
  document.getElementById('contact-detail-section')!.classList.remove('hidden');

  // 授業情報を取得
  const lesson = allLessons.find((l) => l.id === lessonId);
  if (!lesson) {
    showMessage('授業が見つかりません');
    return;
  }

  const dateLabel = formatLessonDateShort(lesson.date!);
  const detailHeading = document.getElementById('contact-detail-heading') as HTMLElement;
  detailHeading.textContent = `${dateLabel} の連絡帳`;
  detailHeading.setAttribute('tabindex', '-1');
  detailHeading.focus();

  // 動的ラベル更新
  try {
    const names = await getUserDisplayNames();
    const instructorLabel = document.getElementById('contact-instructor-label');
    const studentLabel = document.getElementById('contact-student-label');
    if (instructorLabel) instructorLabel.textContent = `${names.instructor}より`;
    // 該当授業のstudentOwnerに対応する生徒名を表示
    if (studentLabel) {
      const studentName = lesson.studentOwner
        ? (contactStudentNameMap.get(lesson.studentOwner) || names.student)
        : names.student;
      studentLabel.textContent = `${studentName}より`;
    }
  } catch {
    // フォールバック: 固定ラベルのまま
  }

  // ContactNote を取得
  try {
    const { data: notes } = await getClient().models.ContactNote.list({
      filter: { lessonId: { eq: lessonId } },
    });
    const note = notes && notes.length > 0 ? notes[0] : null;
    currentNoteId = note?.id || null;

    renderNoteField('instructor', note?.instructorNote || null);
    renderNoteField('student', note?.studentNote || null);

    // ロールに応じて編集ボタンの表示を制御
    const role = getCurrentUserRole();
    const instructorEditBtn = document.querySelector('[data-edit-field="instructor"]') as HTMLElement;
    const studentEditBtn = document.querySelector('[data-edit-field="student"]') as HTMLElement;
    if (instructorEditBtn) {
      instructorEditBtn.style.display = canEditInstructorNote(role) ? '' : 'none';
    }
    if (studentEditBtn) {
      studentEditBtn.style.display = canEditStudentNote(role) ? '' : 'none';
    }
  } catch (error) {
    console.error('Load contact note error:', error);
    showMessage('連絡帳の読み込みに失敗しました');
  }
}

function renderNoteField(field: 'instructor' | 'student', content: string | null) {
  const displayEl = document.getElementById(`contact-${field}-display`) as HTMLElement;
  const editEl = document.getElementById(`contact-${field}-edit`) as HTMLElement;
  const contentEl = document.getElementById(`contact-${field}-content`) as HTMLElement;

  editEl.classList.add('hidden');
  displayEl.classList.remove('hidden');

  if (content && content.trim()) {
    contentEl.textContent = content;
    contentEl.style.whiteSpace = 'pre-wrap';
  } else {
    contentEl.textContent = '（まだ記入されていません）';
    contentEl.style.whiteSpace = 'normal';
  }
}

function startEdit(field: string) {
  const displayEl = document.getElementById(`contact-${field}-display`) as HTMLElement;
  const editEl = document.getElementById(`contact-${field}-edit`) as HTMLElement;
  const textarea = document.getElementById(`contact-${field}-textarea`) as HTMLTextAreaElement;
  const contentEl = document.getElementById(`contact-${field}-content`) as HTMLElement;

  // 現在の内容をテキストエリアに設定
  const currentContent = contentEl.textContent;
  if (currentContent && currentContent !== '（まだ記入されていません）') {
    textarea.value = currentContent;
  } else {
    textarea.value = '';
  }

  displayEl.classList.add('hidden');
  editEl.classList.remove('hidden');
  textarea.focus();
}

function cancelEdit(field: string) {
  const displayEl = document.getElementById(`contact-${field}-display`) as HTMLElement;
  const editEl = document.getElementById(`contact-${field}-edit`) as HTMLElement;

  editEl.classList.add('hidden');
  displayEl.classList.remove('hidden');

  // 編集ボタンにフォーカスを戻す
  const editBtn = displayEl.querySelector(`[data-edit-field="${field}"]`) as HTMLElement;
  if (editBtn) editBtn.focus();
}

async function saveNote(field: string) {
  const textarea = document.getElementById(`contact-${field}-textarea`) as HTMLTextAreaElement;
  const value = textarea.value.trim() || null;

  try {
    if (currentNoteId) {
      // 既存のノートを更新
      const updateData: Record<string, unknown> = { id: currentNoteId };
      if (field === 'instructor') {
        updateData.instructorNote = value;
      } else {
        updateData.studentNote = value;
      }
      await getClient().models.ContactNote.update(updateData as any);
    } else {
      // 新規作成
      const createData: Record<string, unknown> = { lessonId: currentLessonId };
      if (field === 'instructor') {
        createData.instructorNote = value;
      } else {
        createData.studentNote = value;
      }
      const { data: created } = await getClient().models.ContactNote.create(createData as any);
      if (created) {
        currentNoteId = created.id;
      }
    }

    showMessage('保存しました', 'success');
    renderNoteField(field as 'instructor' | 'student', value);
  } catch (error) {
    console.error('Save note error:', error);
    showMessage('保存に失敗しました');
  }
}
